import { useIntl } from "react-intl";
import moment from 'moment';
import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect, useRef } from "react";

//Components
import { Grid, Typography, Container, Paper, Divider } from "@mui/material";
import { NotificationManager } from 'Components/Notifications';
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import Filters from '../.components/filters';
import { NavLink } from "react-router-dom";

//Actions
import { sdCardStatusRequest, clearRedux } from 'Redux/actions';

//Helpers
import cx from 'classnames';
import { getDateFormatted } from 'Core/data/Helpers';

const ROWS_PER_PAGE = 20;
const RANGE_MODE_ROWS = 50;
const FORCE_CURRENT_WEEK_RANGE = false; // true = always use current week dates on mount, ignoring saved preferences

const SdCardStatus = (props) => {
    const { timezone } = props;
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const tableRef = useRef();
    const rawDataFilters = useSelector(state => state.rawDataTabRedux.filters);

    const [columns, setColumns] = useState([
        {
            key: "camerasn",
            title: messages['vidfleet_camera_sm'],
            show: true,
            locked: true,
            resize: true,
            export: true,
            exportCell: "cameraSN",
            code: "vidfleet_camera_sm",
            render: (item) => {
                return (
                    <NavLink to={`/app/devices/overview/${item?.cameraSN}`} style={{ color: 'black' }}>
                        <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.cameraSN}</span>
                    </NavLink>
                )
            }
        },
        {
            key: "previousstatus",
            title: messages['vidfleet_previous_status'],
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: "previousStatus",
            code: "vidfleet_previous_status",
            render: (item) => {
                return <span>{item?.previousStatus}</span>
            }
        },
        {
            key: "currentstatus",
            title: messages['vidfleet_current_status'],
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: "currentStatus",
            code: "vidfleet_current_status",
            render: (item) => {
                return <span>{item?.currentStatus}</span>
            }
        },
        {
            key: "changetime",
            title: messages['vidfleet_change_time'],
            show: true,
            locked: false,
            resize: true,
            export: true,
            exportCell: "changeTime",
            exportFormat: "datetime",
            code: "vidfleet_change_time",
            render: (item) => {
                return <span>{item?.changeTime && getDateFormatted(item?.changeTime, "date", "datetime", null, timezone)}</span>
            }
        },
    ]);

    const current_week = moment().startOf('week');
    const tempStartDate = new Date(current_week.toString());
    const [startDate, setStartDate] = useState(FORCE_CURRENT_WEEK_RANGE ? tempStartDate : (rawDataFilters?.startDate || tempStartDate));
    const [endDate, setEndDate] = useState(FORCE_CURRENT_WEEK_RANGE ? new Date() : (rawDataFilters?.endDate || new Date()));
    const [beforeRangeDate, setBeforeRangeDate] = useState({
        start: tempStartDate,
        end: endDate
    })
    const [last, setLast] = useState(rawDataFilters?.last || { id: '20', name: '20' });
    const [type, setType] = useState(rawDataFilters?.type || 'last');
    const [esn, setEsn] = useState(props?.single ? props?.device?.esn || "" : "");

    const { loading, Items, hasMore, error, errorCode } = useSelector(state => state.sdCardStatusRedux);

    const getData = (refresh, paramType) => {
        const currentType = paramType ?? type;
        if (refresh) {
            dispatch(clearRedux("SD_CARD_STATUS", { hasMore: false, Items: [] }))
        }

        let currentStarDate = new Date(startDate);
        let currentEndDate = new Date(endDate);

        let payload = {
            startDate: currentStarDate.setHours(0),
            endDate: currentEndDate.setHours(23, 59, 59),
            from: 0,
            limit: currentType === "last" ? last.id : ROWS_PER_PAGE,
            moreRecords: false,
            hasMore: refresh ? false : currentType === "last" ? false : hasMore,
            filterByDate: currentType === "range",
            esn: esn
        }

        if(payload.startDate && payload.endDate){
            payload.startDate = new Date(payload.startDate)
            payload.endDate = new Date(payload.endDate)
        }

        dispatch(sdCardStatusRequest(payload));
        tableRef?.current?.conditionsHandler(payload)
    }

    const handleLast = (type) => {
        dispatch(clearRedux("SD_CARD_STATUS", { hasMore: false }))
        setType(type);
        if (type === 'range') {
            tableRef?.current?.setRows(RANGE_MODE_ROWS);
        }
        getData(true, type);
    }

    const handleChangeConditions = (tags, newConditions, total, newColumns, rows, sort, key, fromPreferences) => {
        let currentStartDate = new Date(startDate);
        let currentEndDate = new Date(endDate);
        let currentType = type;

        // Sync local state from preferences when loaded from saved preferences
        if (fromPreferences && newConditions) {
            if (newConditions.filterByDate && newConditions.startDate) {
                currentType = 'range';
                setType('range');
                if (!FORCE_CURRENT_WEEK_RANGE) {
                    currentStartDate = new Date(newConditions.startDate);
                    currentEndDate = new Date(newConditions.endDate);
                    setStartDate(currentStartDate);
                    setEndDate(currentEndDate);
                    setBeforeRangeDate({
                        start: currentStartDate,
                        end: currentEndDate
                    });
                }
            } else if (newConditions.limit) {
                currentType = 'last';
                setType('last');
                setLast({ id: String(newConditions.limit), name: String(newConditions.limit) });
            }
        }

        let payload = {
            startDate: new Date(currentStartDate).setHours(0),
            endDate: new Date(currentEndDate).setHours(23, 59, 59),
            from: 0,
            limit: currentType === "last" ? newConditions?.limit || last.id : ROWS_PER_PAGE,
            moreRecords: false,
            hasMore: false,
            type: currentType,
            filterByDate: currentType === "range",
            esn: esn,
        };

        if (payload.startDate && payload.endDate) {
            payload.startDate = new Date(payload.startDate);
            payload.endDate = new Date(payload.endDate);
        }

        dispatch(sdCardStatusRequest(payload));
    };

    useEffect(() => {
       
        return () => {
            dispatch(clearRedux("SD_CARD_STATUS"));
        };
    }, []);

    useEffect(() => {
        if (error) {
            if (esn) {
                NotificationManager.error(
                    errorCode === 403 ? messages['vidfleet_the_esn_could_not_be_consulted'] : messages['vidfleet_the_esn_does_not_exist'],
                    messages['vidfleet_sd_card_status'],
                    3000,
                    null,
                    null,
                    'error'
                );
            } else {
                NotificationManager.error(
                    messages['vidfleet_error_sd_card_status'],
                    messages['vidfleet_sd_card_status'],
                    3000,
                    null,
                    null,
                    'error'
                );
            }
            dispatch(clearRedux("SD_CARD_STATUS", { error: null, errorCode: null }));
        }
    }, [error]);

    return (
        <Container maxWidth={false} className={cx("vidfleet-sd-card-status-tab", !props?.single ? "container-view" : "container-view-tab")}>
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ padding: !props?.single ? "14px 18px" : 0, borderBottom: "1px solid #e8e8e8", height: '95v' }}
                >
                    {
                        !props?.single &&
                        <Grid item xs={6}>
                            <Typography variant="h5" gutterBottom component="div">
                                {messages['vidfleet_sd_card_status']}
                            </Typography>
                        </Grid>
                    }
                    {
                        !props?.single &&
                        <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                            <Divider style={{ backgroundColor: '#999999' }} />
                        </div>
                    }
                    <DataViewInfiniteScroll
                        ref={tableRef}
                        name="pm-sdcard-status-v1"
                        showTotalCount={false}
                        exportConfig={{
                            fileName: "sdcardstatuses",
                            disablePDF: true,
                            useDataGrid: true,
                        }}
                        externalDataConfig={{
                            loading,
                            data: Items,
                            onRefresh: () => getData(true),
                            nextToken: hasMore,
                            totalCount: Items?.length || 0,
                            totalCountLoading: loading,
                        }}
                        columns={columns}
                        uiConfig={{
                            topHeight: props?.single ? 286 : 200,
                            hideSplit: true,
                            hideFilters: true,
                        }}
                        searchConfig={{
                            disabled: true,
                        }}
                        selectionConfig={{
                            disabled: true,
                        }}
                        tableButtons={["export", 'orderColumn']}
                        extraFilters={() => {
                            return (
                                <Filters
                                    startDate={startDate}
                                    endDate={endDate}
                                    setStartDate={setStartDate}
                                    setEndDate={setEndDate}
                                    setBeforeRangeDate={setBeforeRangeDate}
                                    beforeRangeDate={beforeRangeDate}
                                    last={last}
                                    setLast={setLast}
                                    onChangeFilters={() => getData(true)}
                                    type={type}
                                    setType={handleLast}
                                    esn={esn}
                                    setEsn={setEsn}
                                    disabled={loading}
                                    single={props?.single}
                                    module={props?.module}
                                    setModule={props?.setModule}
                                />
                            )
                        }}
                        onChangeColumns={(items) => setColumns(items)}
                        infiniteScroll={type !== 'last'}
                        onChangePage={() => getData()}
                        flexEnd
                        onChangeConditions={handleChangeConditions}
                    />
                </Grid>
            </Paper>
        </Container>
    )
}

export default SdCardStatus;
